<template>
	<header class="fixed md:sticky top-0 z-50 w-full">
		<DoorsaleBanner />
		<div class="bg-surface-light border-b border-surface-grey md:py-4">
			<BaseContainer>
				<div
					class="flex flex-col md:flex-row justify-between items-center w-full max-w-100 mx-auto md:max-w-full"
				>
					<RouterLink
						to="/doorsale"
						:class="[
							'transition-all flex-shrink-0 origin-center ease-linear flex items-center md:p-0 md:h-12 md:mb-0 md:w-50',
							showLogo ? 'w-35 h-16 pt-4' : 'py-0 w-0 h-0',
						]"
					>
						<GlobalCustomerLogo />
					</RouterLink>

					<div
						:class="[
							'grid grid-cols-3 mx-auto md:flex items-center transition-all overflow-hidden justify-center md:h-auto md:p-0 md:flex-1 w-full md:justify-end',
							scrollingDown && lastScrollY > 100 ? 'h-0 py-0' : 'h-18 py-4',
						]"
					>
						<div
							class="flex items-center justify-center"
							v-for="(step, index) in steps"
							:key="index"
						>
							<div
								class="flex flex-col md:flex-row items-center gap-1 md:gap-2"
							>
								<div
									:class="[
										'w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center text-xs md:text-sm font-semibold',
										currentStep == 3 && index == 2
											? 'bg-success text-white'
											: currentStep == index + 1
												? 'bg-primary text-primary-text'
												: 'bg-surface-grey text-text opacity-50',
									]"
								>
									<CheckmarkIcon
										v-if="currentStep > index + 1 || (currentStep == 3 && index == 2)"
										class="w-3 h-3 fill-current"
									/>
									<span v-else>{{ index + 1 }}</span>
								</div>

								<span
									:class="[
										'text-xs md:text-sm font-semibold',
										currentStep == 3 && index == 2
											? 'text-success'
											: currentStep == index + 1
												? 'text-primary'
												: 'text-text opacity-50',
									]"
								>
									{{ step }}
								</span>
							</div>

							<div
								v-if="index < steps.length - 1"
								class="w-10 h-px bg-surface-grey ml-4 mr-4 hidden md:block"
							/>
						</div>
					</div>
				</div>
			</BaseContainer>
		<div
			v-if="route.name !== 'doorsale-seats' && cart.cartExpire > 0"
			ref="countdownEl"
			class="w-full flex md:hidden grow items-center gap-1 font-semibold bg-surface-grey-light text-sm border-t border-surface-grey px-4 py-2"
		>
			<StopwatchIcon class="fill-primary w-6 h-6" />
			<BaseCountdown :date="cart.cartExpire" /> {{ $t('checkout.countdown') }}
		</div>
	</div>
		<DoorsaleProgressBar v-if="eventId !== undefined" :event-id="eventId" />
	</header>
	<div :class="['md:h-0', calculateHeaderHeight()]"></div>
	<div class="md:hidden" :style="{ height: `${countdownHeight}px` }" aria-hidden="true"></div>
</template>

<script setup lang="ts">
	import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
	import { useI18n } from 'vue-i18n';
	import { useRoute } from 'vue-router';

	import GlobalCustomerLogo from '@/components/globals/GlobalCustomerLogo.vue';
	import CheckmarkIcon from '@/components/icons/CheckmarkIcon.vue';
	import StopwatchIcon from '@/components/icons/StopwatchIcon.vue';
	import BaseContainer from '@/components/ui/BaseContainer.vue';
	import BaseCountdown from '@/components/ui/BaseCountdown.vue';
	import { useCartStore } from '@/stores/cart';

	import DoorsaleBanner from './DoorsaleBanner.vue';
	import DoorsaleProgressBar from './DoorsaleProgressBar.vue';

	const props = defineProps<{
		currentStep: number;
		eventId?: number | string;
	}>();

	const { t } = useI18n();
	const route = useRoute();
	const cart = useCartStore();

	const steps = computed(() => [
		t('doorsale.header_step_tickets'),
		t('doorsale.header_step_customer_info'),
		t('doorsale.header_step_receipt'),
	]);

	const showLogo = ref(true);
	const scrollingDown = ref(false);
	const lastScrollY = ref(0);
	const countdownEl = ref<HTMLElement | null>(null);
	const countdownHeight = ref(0);

	watch(countdownEl, (element, _previous, onCleanup) => {
		countdownHeight.value = element?.getBoundingClientRect().height ?? 0;
		if (!element) return;

		const observer = new ResizeObserver(() => {
			countdownHeight.value = element.getBoundingClientRect().height;
		});
		observer.observe(element, { box: 'border-box' });
		onCleanup(() => observer.disconnect());
	}, { flush: 'post' });

	function calculateHeaderHeight() {
		return props.eventId !== undefined ? 'h-56' : 'h-42';
	}

	function handleScroll() {
		showLogo.value = window.scrollY < 50;
		scrollingDown.value = window.scrollY > lastScrollY.value;
		lastScrollY.value = window.scrollY;
	}

	onMounted(() => {
		window.addEventListener('scroll', handleScroll);
	});

	onUnmounted(() => {
		window.removeEventListener('scroll', handleScroll);
	});
</script>
